import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Button,
  Box,
  Typography,
  Paper,
  IconButton,
  Autocomplete,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  InputAdornment,
  Tabs,
  Tab,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  AutoAwesome as AIIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import MaterialSuggestField from './MaterialSuggestField';
import ExcelImportReview from './ExcelImportReview';

const GST_SLABS = [5, 12, 18, 28];

// Lightweight client-side fuzzy match for Excel import — normalizes and does
// a simple substring/prefix check against the already-loaded catalog, so we
// don't need one network call per row.
const normalizeForMatch = (s) => (s || '').toLowerCase().trim().replace(/\s+/g, ' ').replace(/[()]/g, '').trim();

// Only an EXACT match (after normalization) is confident enough to silently
// auto-link with no review. Anything looser (prefix, fuzzy) must go through
// the review dialog so the user explicitly confirms it — a short typed
// string being a "prefix" of a longer name is not reliable enough to trust
// blindly (e.g. "Dru" is technically a prefix of "Drum" but could easily be
// a typo for something else entirely).
const findCatalogMatch = (typedName, catalogItems) => {
  const normalized = normalizeForMatch(typedName);
  if (!normalized) return null;
  return catalogItems.find(item => normalizeForMatch(item.itemName) === normalized) || null;
};

const BulkMaterialForm = ({
  formData,
  sites,
  departments,
  expenseCategories,
  materialItems,
  filteredExpenseCategories,
  filteredMaterialItems,
  billFile,
  paymentProofFile,
  eWayBillFile,
  onItemsChange,
  onFormDataChange,
  onFileChange,
  setFilteredExpenseCategories,
  setFilteredMaterialItems,
  allFieldSettings
}) => {
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({
    expenseCategoryId: '',
    materialItemId: '',
    customItemDescription: '',
    quantity: '',
    unit: '',
    amount: '',
    gstPercentage: ''
  });
  const [error, setError] = useState('');
  const [entryMode, setEntryMode] = useState('manual'); // 'manual' | 'excel' | 'ai'
  const [excelError, setExcelError] = useState('');
  const [excelSuccess, setExcelSuccess] = useState('');
  const [excelReviewOpen, setExcelReviewOpen] = useState(false);
  const [excelReviewRows, setExcelReviewRows] = useState([]);
  const [pendingExcelMeta, setPendingExcelMeta] = useState({ isGST: false });

  // AI Extract states
  const [aiFile, setAiFile] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiSuccess, setAiSuccess] = useState('');
  const [aiPreviewItems, setAiPreviewItems] = useState([]);
  const [aiExtractedMeta, setAiExtractedMeta] = useState(null); // supplier, bill no, date

  const isGST = formData.billType === 'GST' && formData.billStatus !== 'pending';

  useEffect(() => {
    if (formData.billStatus === 'pending' && entryMode === 'ai') {
      setEntryMode('manual');
    }
  }, [formData.billStatus]);

  const itemGSTAmount = isGST && currentItem.amount && currentItem.gstPercentage
    ? parseFloat((parseFloat(currentItem.amount) * parseFloat(currentItem.gstPercentage) / 100).toFixed(2))
    : 0;
  const itemTotalAmount = isGST && currentItem.amount && currentItem.gstPercentage
    ? parseFloat((parseFloat(currentItem.amount) + itemGSTAmount).toFixed(2))
    : parseFloat(currentItem.amount || 0);

  const materialOnlyExpenseCategories = filteredExpenseCategories.filter(
    cat => allFieldSettings?.[cat.id]?.formType !== 'employee_expense'
  );

  const catSettings = allFieldSettings?.[parseInt(currentItem.expenseCategoryId)];
  const materialItemMode = catSettings?.materialItemMode || 'dropdown';
  const showDropdown = materialItemMode === 'dropdown' || materialItemMode === 'both';
  const showFreeText = materialItemMode === 'freetext' || materialItemMode === 'both';
  const showMaterialField = materialItemMode !== 'hidden';

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    onFormDataChange({ target: { name, value } });
    if (name === 'departmentId') {
      setFilteredExpenseCategories(expenseCategories);
      setCurrentItem({ expenseCategoryId: '', materialItemId: '', customItemDescription: '', quantity: '', unit: '', amount: '', gstPercentage: '' });
      setFilteredMaterialItems([]);
    }
  };

  const handleCurrentItemChange = (e) => {
    const { name, value } = e.target;
    if (name === 'expenseCategoryId') {
      const filtered = materialItems.filter(item =>
        item.departmentId === parseInt(formData.departmentId) &&
        item.expenseCategoryId === parseInt(value)
      );
      setFilteredMaterialItems(filtered);
      setCurrentItem(prev => ({ ...prev, expenseCategoryId: value, materialItemId: '', customItemDescription: '', unit: '' }));
      return;
    }
    if (name === 'materialItemId') {
      const item = materialItems.find(i => i.id === parseInt(value));
      setCurrentItem(prev => ({ ...prev, materialItemId: value, unit: item?.unit || prev.unit }));
      return;
    }
    setCurrentItem(prev => ({ ...prev, [name]: value }));
  };

  const handleAddItem = () => {
    if (!currentItem.expenseCategoryId) { setError('Please select an expense category'); return; }
    if (showDropdown && !showFreeText && !currentItem.materialItemId) { setError('Please select a material item'); return; }
    if (showFreeText && !showDropdown && !currentItem.customItemDescription) { setError('Please enter a material description'); return; }
    if (showDropdown && showFreeText && !currentItem.materialItemId && !currentItem.customItemDescription) { setError('Please select a material item or enter a custom description'); return; }
    if ((!catSettings || catSettings.quantityRequired) && !currentItem.quantity) { setError('Quantity is required'); return; }
    if ((!catSettings || catSettings.amountRequired) && !currentItem.amount) { setError('Amount is required'); return; }
    if (isGST && (!catSettings || catSettings.gstSlabRequired) && !currentItem.gstPercentage) { setError('Please select a GST slab for this item'); return; }

    const expenseCategory = expenseCategories.find(c => c.id === parseInt(currentItem.expenseCategoryId));
    const materialItem = materialItems.find(m => m.id === parseInt(currentItem.materialItemId));
    const baseAmt = parseFloat(currentItem.amount);
    const gstPct = isGST && currentItem.gstPercentage ? parseFloat(currentItem.gstPercentage) : 0;
    const gstVal = parseFloat((baseAmt * gstPct / 100).toFixed(2));
    const totalAmt = isGST ? parseFloat((baseAmt + gstVal).toFixed(2)) : baseAmt;

    const newItem = {
      ...currentItem,
      id: Date.now(),
      expenseCategoryName: expenseCategory?.category_name,
      materialItemName: materialItem?.itemName || currentItem.customItemDescription,
      baseAmount: isGST ? baseAmt : null,
      gstAmount: isGST ? gstVal : null,
      totalAmount: totalAmt,
      gstPercentage: isGST ? currentItem.gstPercentage : null
    };

    const updatedItems = [...items, newItem];
    setItems(updatedItems);
    onItemsChange(updatedItems);
    setCurrentItem({ expenseCategoryId: currentItem.expenseCategoryId, materialItemId: '', customItemDescription: '', quantity: '', unit: '', amount: '', gstPercentage: '' });
    setError('');
  };

  const handleRemoveItem = (itemId) => {
    const updatedItems = items.filter(item => item.id !== itemId);
    setItems(updatedItems);
    onItemsChange(updatedItems);
  };

  const calculateTotal = () => items.reduce((sum, item) => sum + (item.totalAmount || parseFloat(item.amount || 0)), 0);

  const handleExcelReviewConfirm = (resolvedRows) => {
    const { isGST } = pendingExcelMeta;
    const finalItems = resolvedRows.map((r) => {
      const gstPct = isGST ? r.gstPercentage : null;
      if (r.useExisting) {
        return {
          id: Date.now() + r.rowIndex,
          expenseCategoryId: r.useExisting.expenseCategoryId || '',
          expenseCategoryName: 'From Excel (matched)',
          materialItemId: r.useExisting.id,
          customItemDescription: '',
          materialItemName: r.useExisting.itemName,
          quantity: r.quantity, unit: r.unit, amount: r.amount,
          baseAmount: r.baseAmount, gstPercentage: gstPct, gstAmount: r.gstAmount, totalAmount: r.totalAmount
        };
      }
      return {
        id: Date.now() + r.rowIndex,
        expenseCategoryId: '', expenseCategoryName: 'From Excel',
        materialItemId: '', customItemDescription: r.materialName, materialItemName: r.materialName,
        quantity: r.quantity, unit: r.unit, amount: r.amount,
        baseAmount: r.baseAmount, gstPercentage: gstPct, gstAmount: r.gstAmount, totalAmount: r.totalAmount
      };
    });
    const updatedItems = [...items, ...finalItems];
    setItems(updatedItems);
    onItemsChange(updatedItems);
    const linkedCount = resolvedRows.filter(r => r.useExisting).length;
    setExcelSuccess(`✅ ${finalItems.length} item(s) imported from Excel!${linkedCount > 0 ? ` (${linkedCount} linked to existing catalog items)` : ''}`);
    setExcelReviewOpen(false);
    setExcelReviewRows([]);
  };

  const handleExcelReviewCancel = () => {
    setExcelReviewOpen(false);
    setExcelReviewRows([]);
  };

  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();
    const headers = ['Material Name / Description', 'Quantity', 'Unit', isGST ? 'Base Amount (before GST)' : 'Amount', ...(isGST ? ['GST % (5/12/18/28)'] : [])];
    const sampleRow = ['Cement Bags', '10', 'Bags', '5000', ...(isGST ? ['18'] : [])];
    const ws = XLSX.utils.aoa_to_sheet([headers, sampleRow]);
    ws['!cols'] = headers.map(() => ({ wch: 25 }));
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, 'bulk_material_template.xlsx');
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setExcelError(''); setExcelSuccess('');
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(new Uint8Array(evt.target.result), { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
        if (data.length < 2) { setExcelError('Excel file is empty or has no data rows'); return; }
        const newItems = []; const errors = []; let matchedCount = 0;
        for (let i = 1; i < data.length; i++) {
          const row = data[i];
          if (!row || row.length === 0 || !row[0]) continue;
          const materialName = String(row[0] || '').trim();
          const quantity = parseFloat(row[1]);
          const unit = String(row[2] || '').trim();
          const amount = parseFloat(row[3]);
          const gstPct = isGST ? parseFloat(row[4]) : null;
          if (!materialName) { errors.push(`Row ${i + 1}: Material name is required`); continue; }
          if (isNaN(quantity) || quantity <= 0) { errors.push(`Row ${i + 1}: Invalid quantity`); continue; }
          if (!unit) { errors.push(`Row ${i + 1}: Unit is required`); continue; }
          if (isNaN(amount) || amount <= 0) { errors.push(`Row ${i + 1}: Invalid amount`); continue; }
          if (isGST && (isNaN(gstPct) || ![5, 12, 18, 28].includes(gstPct))) { errors.push(`Row ${i + 1}: GST % must be 5, 12, 18, or 28`); continue; }
          const gstVal = isGST ? parseFloat((amount * gstPct / 100).toFixed(2)) : 0;
          const totalAmt = isGST ? parseFloat((amount + gstVal).toFixed(2)) : amount;
          const catalogMatch = findCatalogMatch(materialName, materialItems || []);
          newItems.push({
            rowIndex: i, materialName, matchedItem: catalogMatch || null,
            quantity: quantity.toString(), unit, amount: amount.toString(),
            gstPercentage: isGST ? gstPct : null,
            baseAmount: isGST ? amount : null, gstAmount: isGST ? gstVal : null, totalAmount: totalAmt
          });
          if (catalogMatch) matchedCount++;
        }
        if (errors.length > 0) setExcelError(`Some rows had errors:\n${errors.join('\n')}`);
        if (newItems.length > 0) {
          setPendingExcelMeta({ isGST });
          setExcelReviewRows(newItems);
          setExcelReviewOpen(true);
        }
      } catch (err) { setExcelError('Failed to read Excel file. Please use the template format.'); }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  // ─── AI EXTRACT ───────────────────────────────────────────────
  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleAiFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAiFile(file);
    setAiPreviewItems([]);
    setAiError('');
    setAiSuccess('');
    setAiExtractedMeta(null);
    e.target.value = '';
  };

  const handleAiExtract = async () => {
    if (!aiFile) { setAiError('Please select a PDF or image file first'); return; }
    setAiLoading(true);
    setAiError('');
    setAiSuccess('');
    setAiPreviewItems([]);
    setAiExtractedMeta(null);

    try {
      const base64 = await toBase64(aiFile);
      const isPdf = aiFile.type === 'application/pdf';
      const mediaType = isPdf ? 'application/pdf' : aiFile.type;

      const prompt = `You are extracting material/item data from a GST tax invoice or bill.

Extract ALL line items (materials/goods) from this invoice. For each item extract:
- material_name: full description of the item
- quantity: numeric value only
- unit: unit of measurement (Pcs, Kg, Mtr, Bags, Nos, Roll, etc.)
- base_amount: the taxable/base amount for that item (before GST), numeric only
- gst_percentage: GST rate as number (5, 12, 18, or 28). If not exactly one of these, round to nearest. If no GST, use 0.

Also extract from the invoice header:
- supplier_name: name of the seller/supplier
- invoice_number: invoice/bill number
- invoice_date: date in YYYY-MM-DD format

IMPORTANT:
- Skip freight, labour, tax summary rows, CGST/SGST rows — only include actual material/goods line items
- If quantity is in combined format like "5.000 Pcs" extract just the number
- Return ONLY valid JSON, no explanation, no markdown, no backticks

Return this exact JSON structure:
{
  "supplier_name": "",
  "invoice_number": "",
  "invoice_date": "",
  "items": [
    {
      "material_name": "",
      "quantity": 0,
      "unit": "",
      "base_amount": 0,
      "gst_percentage": 18
    }
  ]
}`;

      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1'}/materials/ai-exBulkMaterialFormct`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-20250514',
          max_tokens: 4000,
          messages: [{
            role: 'user',
            content: [
              {
                type: isPdf ? 'document' : 'image',
                source: { type: 'base64', media_type: mediaType, data: base64 }
              },
              { type: 'text', text: prompt }
            ]
          }]
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error?.message || 'API error');

      const rawText = data.content?.find(b => b.type === 'text')?.text || '';

      // Clean and parse JSON
      const cleaned = rawText.replace(/```json|```/g, '').trim();
      const parsed = JSON.parse(cleaned);

      if (!parsed.items || parsed.items.length === 0) {
        setAiError('No items could be extracted from this file. Please check the file quality.');
        setAiLoading(false);
        return;
      }

      // Build preview items
      const preview = parsed.items.map((item, idx) => {
        const baseAmt = parseFloat(item.base_amount) || 0;
        const gstPct = [5, 12, 18, 28].includes(parseInt(item.gst_percentage)) ? parseInt(item.gst_percentage) : (parseInt(item.gst_percentage) === 0 ? 0 : 18);
        const gstVal = isGST && gstPct ? parseFloat((baseAmt * gstPct / 100).toFixed(2)) : 0;
        const totalAmt = isGST && gstPct ? parseFloat((baseAmt + gstVal).toFixed(2)) : baseAmt;
        return {
          id: Date.now() + idx,
          expenseCategoryId: '',
          expenseCategoryName: 'From AI Extract',
          materialItemId: '',
          customItemDescription: item.material_name,
          materialItemName: item.material_name,
          quantity: String(item.quantity),
          unit: item.unit || '',
          amount: String(baseAmt),
          baseAmount: isGST ? baseAmt : null,
          gstPercentage: isGST && gstPct ? gstPct : null,
          gstAmount: isGST ? gstVal : null,
          totalAmount: totalAmt,
          // editable flag for preview
          _editing: false
        };
      });

      setAiPreviewItems(preview);
      setAiExtractedMeta({
        supplierName: parsed.supplier_name || '',
        invoiceNumber: parsed.invoice_number || '',
        invoiceDate: parsed.invoice_date || ''
      });
      setAiSuccess(`✅ ${preview.length} item(s) extracted! Review below and click "Add All to List".`);

      // Auto-fill supplier, bill number, date if fields are empty
      if (parsed.supplier_name && !formData.supplierName) {
        onFormDataChange({ target: { name: 'supplierName', value: parsed.supplier_name } });
      }
      if (parsed.invoice_number && !formData.billNumber) {
        onFormDataChange({ target: { name: 'billNumber', value: parsed.invoice_number } });
      }
      if (parsed.invoice_date && formData.entryDate === new Date().toISOString().split('T')[0]) {
        onFormDataChange({ target: { name: 'entryDate', value: parsed.invoice_date } });
      }

    } catch (err) {
      console.error('AI Extract error:', err);
      setAiError('Failed to extract items. Please ensure the file is clear and readable. Error: ' + err.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiPreviewChange = (idx, field, value) => {
    setAiPreviewItems(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      // Recalculate totals if amount or gst changes
      if (field === 'amount' || field === 'gstPercentage') {
        const baseAmt = parseFloat(field === 'amount' ? value : updated.amount) || 0;
        const gstPct = parseInt(field === 'gstPercentage' ? value : updated.gstPercentage) || 0;
        const gstVal = isGST && gstPct ? parseFloat((baseAmt * gstPct / 100).toFixed(2)) : 0;
        updated.baseAmount = isGST ? baseAmt : null;
        updated.gstAmount = isGST ? gstVal : null;
        updated.totalAmount = isGST && gstPct ? parseFloat((baseAmt + gstVal).toFixed(2)) : baseAmt;
      }
      return updated;
    }));
  };

  const handleRemoveAiPreviewItem = (idx) => {
    setAiPreviewItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleAddAllAiItems = () => {
    if (aiPreviewItems.length === 0) return;
    const updatedItems = [...items, ...aiPreviewItems];
    setItems(updatedItems);
    onItemsChange(updatedItems);
    setAiPreviewItems([]);
    setAiSuccess('');
    setAiFile(null);
    setAiExtractedMeta(null);
  };

  return (
    <Grid container spacing={2}>
      {error && (
        <Grid item xs={12}>
          <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
        </Grid>
      )}

      {/* Common Details Section */}
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
          📋 Bill Details (Common for All Items)
        </Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      <Grid item xs={12}>
        <TextField select fullWidth label="Site *" name="siteId" value={formData.siteId} onChange={handleFormChange} required>
          {sites.map((site) => (<MenuItem key={site.id} value={site.id}>{site.siteName}</MenuItem>))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField select fullWidth label="Department *" name="departmentId" value={formData.departmentId} onChange={handleFormChange} required>
          {departments.map((dept) => (<MenuItem key={dept.id} value={dept.id}>{dept.department_name}</MenuItem>))}
        </TextField>
      </Grid>

      <Grid item xs={12} sm={6}>
        <TextField fullWidth label="Supplier Name *" name="supplierName" value={formData.supplierName} onChange={handleFormChange} required />
      </Grid>

      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Bill Status *</FormLabel>
          <RadioGroup row name="billStatus" value={formData.billStatus || 'received'} onChange={handleFormChange}>
            <FormControlLabel value="received" control={<Radio />} label="Bill Received" />
            <FormControlLabel value="pending" control={<Radio />} label="Bill Pending" />
          </RadioGroup>
        </FormControl>
      </Grid>

      {formData.billStatus === 'pending' ? (
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Pending From *" name="billPendingFrom" value={formData.billPendingFrom || ''} onChange={handleFormChange} required
            placeholder="e.g. Vendor, Supervisor, Worker, Company..." />
        </Grid>
      ) : (
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Bill Number *" name="billNumber" value={formData.billNumber} onChange={handleFormChange} required />
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <TextField fullWidth type="date" label="Entry Date *" name="entryDate" value={formData.entryDate} onChange={handleFormChange} InputLabelProps={{ shrink: true }} required />
      </Grid>

      {formData.billStatus !== 'pending' && (
        <Grid item xs={12} sm={6}>
          <FormControl component="fieldset">
            <FormLabel component="legend">Bill Type *</FormLabel>
            <RadioGroup row name="billType" value={formData.billType} onChange={handleFormChange}>
              <FormControlLabel value="GST" control={<Radio />} label="GST Bill" />
              <FormControlLabel value="NON_GST" control={<Radio />} label="Non-GST Bill" />
            </RadioGroup>
          </FormControl>
        </Grid>
      )}

      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Payment Source *</FormLabel>
          <RadioGroup row name="paymentSource" value={formData.paymentSource} onChange={handleFormChange}>
            <FormControlLabel value="COMPANY" control={<Radio />} label="Company" />
            <FormControlLabel value="SUPERVISOR" control={<Radio />} label="Supervisor" />
          </RadioGroup>
        </FormControl>
      </Grid>

      {/* Items Section Header with Tabs */}
      <Grid item xs={12} sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ color: 'primary.main' }}>📦 Add Material Items</Typography>
          <Tabs value={entryMode} onChange={(e, v) => setEntryMode(v)} size="small">
            <Tab label="Manual Entry" value="manual" />
            <Tab label="📊 Excel Upload" value="excel" />
            {formData.billStatus !== 'pending' && <Tab label="✨ AI Extract" value="ai" />}
          </Tabs>
        </Box>
        <Divider sx={{ mb: 2 }} />
      </Grid>

      {/* ── EXCEL UPLOAD ── */}
      {entryMode === 'excel' && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>Upload Items via Excel</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Download the template, fill in your items, and upload. Columns: Material Name, Quantity, Unit, Amount{isGST ? ', GST %' : ''}.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate}>Download Template</Button>
              <Button variant="contained" component="label" startIcon={<UploadIcon />}>
                Upload Excel / CSV File
                <input type="file" hidden accept=".xlsx,.xls,.csv" onChange={handleExcelUpload} />
              </Button>
            </Box>
            {excelError && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setExcelError('')}><pre style={{ margin: 0, whiteSpace: 'pre-wrap', fontSize: '12px' }}>{excelError}</pre></Alert>}
            {excelSuccess && <Alert severity="success" onClose={() => setExcelSuccess('')}>{excelSuccess}</Alert>}
            <Alert severity="info" sx={{ mt: 1 }}>
              <strong>Note:</strong> Excel-imported items will show "From Excel" as category.{isGST && ' GST % must be 5, 12, 18, or 28.'}
            </Alert>
          </Paper>
        </Grid>
      )}

      {/* ── AI EXTRACT ── */}
      {entryMode === 'ai' && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 3, bgcolor: '#f8f4ff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <AIIcon sx={{ color: 'secondary.main' }} />
              <Typography variant="subtitle1" fontWeight={600}>AI Extract from PDF / Image</Typography>
              <Chip label="Powered by Claude AI" size="small" color="secondary" variant="outlined" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload your bill (PDF or photo) and AI will automatically read and extract all material items.
              Supplier name, bill number, and date will also be auto-filled if found.
            </Typography>

            {/* File picker + Extract button */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Button variant="outlined" component="label" startIcon={<UploadIcon />}>
                {aiFile ? aiFile.name : 'Select PDF or Image'}
                <input type="file" hidden accept="image/*,.pdf" onChange={handleAiFileChange} />
              </Button>
              {aiFile && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={aiLoading ? <CircularProgress size={18} color="inherit" /> : <AIIcon />}
                  onClick={handleAiExtract}
                  disabled={aiLoading}
                >
                  {aiLoading ? 'Extracting...' : 'Extract Items'}
                </Button>
              )}
              {aiFile && !aiLoading && (
                <Typography variant="caption" color="text.secondary">
                  {(aiFile.size / 1024).toFixed(0)} KB • {aiFile.type.includes('pdf') ? 'PDF' : 'Image'}
                </Typography>
              )}
            </Box>

            {aiLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
                <CircularProgress size={24} color="secondary" />
                <Typography variant="body2" color="text.secondary">
                  Reading bill and extracting items... this may take 10-20 seconds
                </Typography>
              </Box>
            )}

            {aiError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAiError('')}>{aiError}</Alert>}
            {aiSuccess && !aiLoading && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>{aiSuccess}</Alert>}

            {/* Auto-filled meta info */}
            {aiExtractedMeta && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Auto-detected from bill:</strong>{' '}
                {aiExtractedMeta.supplierName && <>Supplier: <strong>{aiExtractedMeta.supplierName}</strong> &nbsp;</>}
                {aiExtractedMeta.invoiceNumber && <>Invoice: <strong>{aiExtractedMeta.invoiceNumber}</strong> &nbsp;</>}
                {aiExtractedMeta.invoiceDate && <>Date: <strong>{aiExtractedMeta.invoiceDate}</strong></>}
              </Alert>
            )}

            {/* Preview Table — editable */}
            {aiPreviewItems.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                  Review Extracted Items ({aiPreviewItems.length}) — Edit if needed before adding
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead sx={{ bgcolor: 'secondary.50' }}>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Material Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Qty</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Unit</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} align="right">Amount (₹)</TableCell>
                        {isGST && <TableCell sx={{ fontWeight: 600 }}>GST %</TableCell>}
                        {isGST && <TableCell sx={{ fontWeight: 600 }} align="right">Total (₹)</TableCell>}
                        <TableCell align="center">Remove</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {aiPreviewItems.map((item, idx) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <TextField
                              size="small" fullWidth variant="standard"
                              value={item.customItemDescription}
                              onChange={(e) => handleAiPreviewChange(idx, 'customItemDescription', e.target.value)}
                              inputProps={{ style: { fontSize: '0.82rem' } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small" type="number" variant="standard"
                              value={item.quantity}
                              onChange={(e) => handleAiPreviewChange(idx, 'quantity', e.target.value)}
                              inputProps={{ style: { fontSize: '0.82rem', width: 60, textAlign: 'right' } }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small" variant="standard"
                              value={item.unit}
                              onChange={(e) => handleAiPreviewChange(idx, 'unit', e.target.value)}
                              inputProps={{ style: { fontSize: '0.82rem', width: 50 } }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              size="small" type="number" variant="standard"
                              value={item.amount}
                              onChange={(e) => handleAiPreviewChange(idx, 'amount', e.target.value)}
                              inputProps={{ style: { fontSize: '0.82rem', width: 80, textAlign: 'right' } }}
                            />
                          </TableCell>
                          {isGST && (
                            <TableCell>
                              <TextField
                                size="small" select variant="standard"
                                value={item.gstPercentage || 18}
                                onChange={(e) => handleAiPreviewChange(idx, 'gstPercentage', e.target.value)}
                                inputProps={{ style: { fontSize: '0.82rem' } }}
                                sx={{ width: 70 }}
                              >
                                {[0, 5, 12, 18, 28].map(s => <MenuItem key={s} value={s}>{s}%</MenuItem>)}
                              </TextField>
                            </TableCell>
                          )}
                          {isGST && (
                            <TableCell align="right">
                              <Typography variant="caption" fontWeight={600}>
                                ₹{(item.totalAmount || 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                          )}
                          <TableCell align="center">
                            <IconButton size="small" color="error" onClick={() => handleRemoveAiPreviewItem(idx)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AddIcon />}
                  onClick={handleAddAllAiItems}
                  fullWidth
                >
                  Add All {aiPreviewItems.length} Items to List
                </Button>
              </Box>
            )}

            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>Note:</strong> Always review extracted items before submitting. AI may occasionally misread unclear or handwritten bills.
            </Alert>
          </Paper>
        </Grid>
      )}

      {/* ── MANUAL ENTRY ── */}
      {entryMode === 'manual' && (
        <Grid item xs={12}>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth label="Expense Category *" name="expenseCategoryId" value={currentItem.expenseCategoryId} onChange={handleCurrentItemChange} disabled={!formData.departmentId} helperText={!formData.departmentId ? 'Select department first' : ''}>
                  {materialOnlyExpenseCategories.map((cat) => (<MenuItem key={cat.id} value={cat.id}>{cat.category_name}</MenuItem>))}
                </TextField>
              </Grid>

              {showMaterialField && showDropdown && (
                <Grid item xs={12} sm={6}>
                  <Autocomplete fullWidth options={filteredMaterialItems}
                    getOptionLabel={(option) => option.itemName}
                    value={filteredMaterialItems.find(item => item.id === parseInt(currentItem.materialItemId)) || null}
                    onChange={(event, newValue) => handleCurrentItemChange({ target: { name: 'materialItemId', value: newValue ? newValue.id : '' } })}
                    disabled={!currentItem.expenseCategoryId}
                    renderInput={(params) => (
                      <TextField {...params} label={showFreeText ? 'Material Item (Optional)' : 'Material Item *'}
                        helperText={!currentItem.expenseCategoryId ? 'Select expense category first' : 'Type to search...'} />
                    )}
                    isOptionEqualToValue={(option, value) => option.id === value?.id}
                  />
                </Grid>
              )}

              {showMaterialField && showFreeText && (
                <Grid item xs={12} sm={showDropdown ? 12 : 6}>
                  <MaterialSuggestField
                    label={showDropdown ? 'Or type custom description' : 'Material Description *'}
                    value={currentItem.customItemDescription}
                    onChange={(text) => setCurrentItem(prev => ({ ...prev, customItemDescription: text }))}
                    onSelectExisting={(item) => {
                      setFilteredMaterialItems(prev => prev.some(mi => mi.id === item.id) ? prev : [...prev, item]);
                      setCurrentItem(prev => ({
                        ...prev,
                        materialItemId: item.id,
                        customItemDescription: '',
                        unit: item.unit || prev.unit
                      }));
                    }}
                    departmentId={formData.departmentId}
                    expenseCategoryId={currentItem.expenseCategoryId}
                    disabled={!currentItem.expenseCategoryId}
                    placeholder="Type material name or description..."
                    helperText={showDropdown ? 'Use this if item is not in the dropdown above' : ''}
                  />
                </Grid>
              )}

              <Grid item xs={6} sm={3}>
                <TextField fullWidth type="number" label="Quantity *" name="quantity" value={currentItem.quantity} onChange={handleCurrentItemChange} inputProps={{ min: 0, step: 0.01 }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField fullWidth label="Unit *" name="unit" value={currentItem.unit} onChange={handleCurrentItemChange} InputProps={{ readOnly: !!currentItem.unit && !!currentItem.materialItemId }} />
              </Grid>
              <Grid item xs={12} sm={isGST ? 3 : 6}>
                <TextField fullWidth type="number" label={isGST ? 'Base Amount *' : 'Amount *'} name="amount" value={currentItem.amount} onChange={handleCurrentItemChange} inputProps={{ min: 0, step: 0.01 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
              </Grid>
              {isGST && (
                <Grid item xs={12} sm={3}>
                  <TextField select fullWidth label="GST Slab *" name="gstPercentage" value={currentItem.gstPercentage} onChange={handleCurrentItemChange}>
                    {GST_SLABS.map(slab => (<MenuItem key={slab} value={slab}>{slab}%</MenuItem>))}
                  </TextField>
                </Grid>
              )}
              {isGST && currentItem.amount && currentItem.gstPercentage && (
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">Base Amount</Typography>
                      <Typography variant="body2">₹{parseFloat(currentItem.amount).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2" color="text.secondary">GST ({currentItem.gstPercentage}%)</Typography>
                      <Typography variant="body2" color="text.secondary">+ ₹{itemGSTAmount.toLocaleString()}</Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" fontWeight="bold">Total</Typography>
                      <Typography variant="body2" fontWeight="bold" color="primary.main">₹{itemTotalAmount.toLocaleString()}</Typography>
                    </Box>
                  </Paper>
                </Grid>
              )}
              <Grid item xs={12}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem} fullWidth disabled={!formData.departmentId}>
                  Add Item to List
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      )}

      {/* Items List */}
      {items.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Added Items ({items.length})</Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Material</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.expenseCategoryName}</TableCell>
                    <TableCell>{item.materialItemName || item.customItemDescription}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell>{item.unit}</TableCell>
                    <TableCell align="right">
                      ₹{(item.totalAmount || parseFloat(item.amount)).toLocaleString()}
                      {item.gstPercentage && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          ₹{parseFloat(item.amount).toLocaleString()} + {item.gstPercentage}% GST
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleRemoveItem(item.id)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={4} align="right"><Typography variant="subtitle1" fontWeight="bold">Total:</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle1" fontWeight="bold" color="primary">₹{calculateTotal().toLocaleString()}</Typography></TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      )}

      {/* Attachments */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>📎 Attachments</Typography>
        <Divider sx={{ mb: 2 }} />
      </Grid>
      {formData.billStatus === 'pending' ? (
        <Grid item xs={12}>
          <Alert severity="info">
            Bill Number, Bill Type, GST, and Bill Upload can be filled in later from the Pending Bills tab once the bill is received.
          </Alert>
        </Grid>
      ) : (
        <Grid item xs={12}>
          <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}>
            {billFile ? billFile.name : 'Upload Bill (Image/PDF) *'}
            <input type="file" hidden accept="image/*,.pdf" onChange={(e) => onFileChange(e, 'bill')} required />
          </Button>
        </Grid>
      )}
      <Grid item xs={12}>
        <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}
          color={eWayBillFile ? 'success' : 'inherit'}>
          {eWayBillFile ? eWayBillFile.name : 'Upload E-Way Bill (Optional)'}
          <input type="file" hidden accept="image/*,.pdf" onChange={(e) => onFileChange(e, 'eWayBill')} />
        </Button>
        {eWayBillFile && (
          <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
            ✅ E-Way Bill attached
          </Typography>
        )}
      </Grid>
      {formData.paymentSource === 'SUPERVISOR' && (
        <Grid item xs={12}>
          <Button variant="outlined" component="label" fullWidth startIcon={<AttachFileIcon />}>
            {paymentProofFile ? paymentProofFile.name : 'Upload Payment Proof *'}
            <input type="file" hidden accept="image/*,.pdf" onChange={(e) => onFileChange(e, 'payment')} required />
          </Button>
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField fullWidth multiline rows={2} label="Remarks (Optional)" name="remarks" value={formData.remarks} onChange={handleFormChange} placeholder="Any additional notes..." />
      </Grid>
      {items.length === 0 && (
        <Grid item xs={12}><Alert severity="warning">Please add at least one item before submitting</Alert></Grid>
      )}

      <ExcelImportReview
        open={excelReviewOpen}
        rows={excelReviewRows}
        departmentId={formData.departmentId}
        onConfirm={handleExcelReviewConfirm}
        onCancel={handleExcelReviewCancel}
      />
    </Grid>
  );
};

export default BulkMaterialForm;